import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Editor as TipTapEditor } from '@tiptap/react'
import { Resizable } from 're-resizable'
import debounce from 'lodash.debounce'
import max from 'lodash.max'

import {
  Label,
  TemplateAttribute,
  useUpdateTemplateElement,
} from '@/services/hooks/template_attributes'
import { Editor } from '@/components/editor/Editor'
import { useDocumentState } from '@/state'
import { useEditDocumentPageLabel } from '@/services/queries/document_label/useEditDocumentPageLabel'
import { cn } from '@/utils'
import { Spinner } from '@/components/ui/spinner'
import { ExtensionsEnum } from '@/components/editor/constants'
import { useAnnotationLayer } from './Annotations/queries'
import { getAbsoluteYFromSubPage } from './Annotations/utils'
import { DocumentPage } from '@/lib/api/client'
import { useAnnotationsContext } from './Annotations/AnnotationsContext'
import { OptionsContextMenu } from '@/components/core/OptionsContextMenu'
import { useParams } from 'wouter'
import { useKeyboardNudge } from '@/utils/hooks/useKeyboardNudge'
import { useMultiSelectDraggable } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectDraggable'
import { useRelativeToAbsolutePositioning } from '@/utils/hooks/useRelativeToAbsolutePositioning'
import { useIsTemplateAttributeMultiSelected } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useIsTemplateAttributeMultiSelected'
import { useMultiSelectKeydownHandler } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectKeydownHandler'
import { useMultiSelectKeyPressed } from '@/pages/DocumentPage/features/templateAttributesMultiSelect/useMultiSelectKeyPressed'

interface OperationLabelProps {
  template: TemplateAttribute<Label>
  isReadOnly: boolean
  documentPage: DocumentPage
  dataType: 'label'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}

export const OperationLabel = ({
  template,
  isReadOnly,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: OperationLabelProps) => {
  const INITIAL_DELTA = useMemo(() => ({ x: 0, y: 0 }), [])

  const editorRef = useRef<TipTapEditor | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const { projectId, documentId } = useParams<{
    projectId: string
    documentId: string
  }>()

  const {
    bringForward,
    bringToFront,
    sendBackward,
    sendToBack,
    isLoading: isOptionsMenuLoading,
  } = useAnnotationLayer(documentPage.id as string)
  const isAddingElement = useDocumentState((state) => state.isAddingElement)
  const selectedElement = useDocumentState((state) => state.selectedElement)
  const croppingImageId = useDocumentState((state) => state.croppingImageId)
  const documentPageId = useMemo(
    () => documentPage.id as string,
    [documentPage],
  )
  const { mutateAsync: updateTemplate, isPending: isUpdatingTemplate } =
    useUpdateTemplateElement({
      documentPageId,
    })
  const zoom = useDocumentState((state) => state.zoom)
  const zoomRatio = useMemo(() => zoom / 100, [zoom])
  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )
  const isSelected = useMemo(
    () =>
      selectedElement?.documentPageId === documentPageId &&
      selectedElement?.id === template.id,
    [selectedElement, documentPageId, template.id],
  )
  const positionData = useMemo(
    () => template?.template_values?.position,
    [template],
  )
  const subPageId = useMemo(
    () => template?.template_values?.subPageId || documentPageId,
    [template, documentPageId],
  )
  const documentSubPages = useMemo(
    () =>
      (documentPage?.template_values?.subPages as Array<string>) ?? [
        documentPage?.id,
      ],
    [documentPage],
  )
  const size = useMemo(() => template?.template_values?.size, [template])
  const { setIsResizing } = useAnnotationsContext()
  const backgroundColor = useMemo(
    () => template?.template_values?.backgroundColor ?? 'transparent',
    [template],
  )
  const updateBackgroundColorHandler = useCallback(
    (color: string) => {
      if (template.id) {
        updateTemplate({
          payload: {
            backgroundColor: color === backgroundColor ? 'transparent' : color,
          },
          data_type: dataType,
          templateAttributeId: template.id,
        }).catch((error) => console.error(error))
      }
    },
    [dataType, template.id, updateTemplate, backgroundColor],
  )

  const updateLabelSizeHandler = useCallback(
    async (
      size: { width: number; height: number },
      position: { x: number; y: number },
    ) => {
      await updateTemplate({
        payload: {
          size,
          position: { ...position, z: positionData?.z },
        },
        data_type: dataType,
        templateAttributeId: template.id as string,
      })
    },
    [dataType, template.id, positionData, updateTemplate],
  )

  const [containerDimensions, setContainerDimensions] = useState({
    width: 0,
    height: 0,
  })

  useEffect(() => {
    let resizeObserver: ResizeObserver
    const minDimensions = { width: 154, height: 25 }
    if (containerRef.current && !size) {
      const rect = containerRef.current.getBoundingClientRect()
      const width = max([rect.width, minDimensions.width])
      const height = max([rect.height, minDimensions.height])
      setContainerDimensions({
        width,
        height,
      })

      resizeObserver = new ResizeObserver((entries) => {
        const { width, height } = entries[0].contentRect

        setContainerDimensions({
          width: max([width, minDimensions.width]),
          height: max([height, minDimensions.height]),
        })
      })

      resizeObserver.observe(containerRef.current)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [containerRef, size])

  useEffect(() => {
    if (size) {
      setContainerDimensions(size)
    }
  }, [size])

  const [{ position, delta }, setPosition] = useState({
    position: positionData,
    delta: INITIAL_DELTA,
  })

  const { mutate: updateLabel } = useEditDocumentPageLabel({
    labelTemplate: template,
    documentPageId,
  })

  const containerId = useMemo(
    () => `${documentPageId}-${template.id}`,
    [documentPageId, template.id],
  )

  const { attributes, isDragging, listeners, transform, setNodeRef } =
    useMultiSelectDraggable({
      disabled: isAddingElement || isReadOnly || !!croppingImageId,
      id: template.id as string,
      data: { type: dataType },
    })

  const { bind, previewDelta } = useKeyboardNudge({
    isActive: isSelected && Boolean(selectedElement?.isRemovable),
    showGridOnNudge: true,
    onNudgeStart,
    onNudgeEnd,
    template,
    documentPage,
  })

  useEffect(() => {
    const tX = transform?.x ?? 0
    const tY = transform?.y ?? 0
    const pX = previewDelta?.x ?? 0
    const pY = previewDelta?.y ?? 0

    // If everything is 0, this is typically the "drag ended" / "idle" state.
    // Don't overwrite delta here — let it be reset only when positionData changes.
    if (tX === 0 && tY === 0 && pX === 0 && pY === 0) {
      return
    }

    setPosition((prevPosition) => ({
      ...prevPosition,
      delta: {
        x: (tX + pX) / zoomRatio,
        y: (tY + pY) / zoomRatio,
      },
    }))
  }, [transform, previewDelta, zoomRatio])

  useEffect(() => {
    setPosition({
      position: positionData,
      delta: INITIAL_DELTA,
    })
  }, [INITIAL_DELTA, positionData])

  const saveLabel = useCallback(
    debounce(({ editor }: { editor: TipTapEditor }) => {
      updateLabel({
        editor,
      })
    }, 500),
    [updateLabel],
  )

  const mutatedListeners = useMemo(
    () => ({
      ...listeners,
      onMouseDown: (e: React.MouseEvent<Element, MouseEvent>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
      onTouchStart: (e: React.TouchEvent<Element>) => {
        const targetId = (e.target as Element)?.id
        if (targetId === containerId) {
          listeners?.onMouseDown(e)
        }
      },
    }),
    [listeners, containerId],
  )

  const isLoading = useMemo(() => isUpdatingTemplate, [isUpdatingTemplate])
  const content = useMemo(() => template?.template_values?.content || '', [])

  const isMultiSelected = useIsTemplateAttributeMultiSelected({
    templateAttributeId: template.id,
  })
  const { setDraggableAnnotationParentRef } = useRelativeToAbsolutePositioning()

  const multiSelectKeydownHandler = useMultiSelectKeydownHandler()

  const isSelectKeyPressed = useMultiSelectKeyPressed()
  const [isEditorFocused, setIsEditorFocused] = useState(false)

  return (
    <OptionsContextMenu
      isReadonly={isReadOnly}
      isDisabled={isOptionsMenuLoading}
      optionsGroups={[
        {
          options: [
            {
              label: 'Bring to front',
              action: () => bringToFront(template?.id as string),
            },
            {
              label: 'Bring forward',
              action: () => bringForward(template?.id as string),
            },
            {
              label: 'Send backward',
              action: () => sendBackward(template?.id as string),
            },
            {
              label: 'Send to back',
              action: () => sendToBack(template?.id as string),
            },
          ],
        },
      ]}
    >
      <div
        id={containerId}
        ref={(el) => {
          setNodeRef(el)
          if (template.id) {
            setDraggableAnnotationParentRef(template.id, el)
          }
        }}
        className={cn('border-4 flex rounded-md box-content absolute', {
          'cursor-move': !isReadOnly,
          'cursor-default': isReadOnly,
          'rounded-md': isSelected || isMultiSelected,
          'shadow-md': isDragging,
          'shadow-gray-200': isDragging,
          'bg-sky-200/10': isDragging,
          'pointer-events-none': isAddingElement,
          'pointer-events-auto': !isAddingElement,
          'z-[100]': isDragging,
        })}
        {...bind}
        {...attributes}
        {...mutatedListeners}
        style={{
          translate: `${delta?.x || 0}px ${delta?.y || 0}px`,
          top: getAbsoluteYFromSubPage({
            subPageId,
            relativeY: position?.y || 0,
            subPageIds: documentSubPages,
          }),
          left: position?.x || 0,
          zIndex: positionData?.z || 1000,
          borderColor:
            isSelected || isMultiSelected
              ? 'rgba(129, 140, 248, 0.5)'
              : 'transparent',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div data-testid="operation-label" ref={containerRef}>
          <Resizable
            size={
              containerDimensions.width > 0 && containerDimensions.height > 0
                ? containerDimensions
                : undefined
            }
            maxWidth={420}
            maxHeight={740}
            minHeight={25}
            minWidth={154}
            onResizeStart={(event, _, ref) => {
              event.stopPropagation()
              const width = ref.clientWidth
              const height = ref.clientHeight
              if (setIsResizing) setIsResizing(true)
              setContainerDimensions({ width, height })
            }}
            onResize={(event, direction, ref) => {
              event.stopPropagation()
              const newWidth = ref.clientWidth
              const newHeight = ref.clientHeight
              const widthDiff = newWidth - containerDimensions.width
              const heightDiff = newHeight - containerDimensions.height

              const deltaUpdates = {
                topLeft: { x: -widthDiff, y: -heightDiff },
                bottomLeft: { x: -widthDiff, y: 0 },
                topRight: { x: 0, y: -heightDiff },
                bottomRight: { x: 0, y: 0 },
              }

              setContainerDimensions({ width: newWidth, height: newHeight })
              setPosition((prevPosition) => ({
                ...prevPosition,
                delta: {
                  x: prevPosition.delta.x + (deltaUpdates[direction]?.x || 0),
                  y: prevPosition.delta.y + (deltaUpdates[direction]?.y || 0),
                },
              }))
            }}
            onResizeStop={(event) => {
              event.stopPropagation()
              if (setIsResizing) setIsResizing(false)
              if (!editorRef.current) return

              if (
                containerDimensions.width < 150 ||
                containerDimensions.height < 45
              )
                return

              updateLabelSizeHandler(
                {
                  width: containerDimensions.width,
                  height: containerDimensions.height,
                },
                {
                  x: (position?.x || 0) + delta.x,
                  y: (position?.y || 0) + delta.y,
                },
              )
                .then(() => {
                  setSelectedElement({
                    documentPageId,
                    id: template?.id as string,
                    isRemovable: true,
                    type: dataType,
                  })
                })
                .catch((error) => {
                  console.error('Error updating label size:', error)
                })
            }}
            handleClasses={{
              topRight: !isReadOnly && isSelected ? 'resize-handle' : '',
              topLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
              bottomRight: !isReadOnly && isSelected ? 'resize-handle' : '',
              bottomLeft: !isReadOnly && isSelected ? 'resize-handle' : '',
            }}
            enable={
              isReadOnly
                ? false
                : {
                    topRight: true,
                    topLeft: true,
                    bottomRight: true,
                    bottomLeft: true,
                  }
            }
          >
            <>
              {isLoading && (
                <div className="absolute h-full w-full bg-zinc-400/60 z-10">
                  <Spinner />
                </div>
              )}
              <Editor
                extensionsList={[
                  ExtensionsEnum.COLOR,
                  ExtensionsEnum.ICONS,
                  ExtensionsEnum.HIGHLIGHT,
                  ExtensionsEnum.MENTION,
                  ExtensionsEnum.UNDERLINE,
                  ExtensionsEnum.BACKGROUND,
                  ExtensionsEnum.SLASH_COMMAND,
                ]}
                projectId={projectId}
                documentId={documentId}
                documentPageId={documentPageId}
                className={cn(
                  'flex-1 py-0 px-1 document-label min-h-0 text-xs bg-transparent',
                  {
                    'pointer-events-none': isAddingElement || isDragging,
                  },
                )}
                onFocus={() => {
                  setIsEditorFocused(true)
                }}
                onBlur={({ editor }) => {
                  setIsEditorFocused(false)
                  saveLabel({ editor })
                }}
                onClick={() => {
                  setSelectedElement({
                    id: template.id as string,
                    documentPageId,
                    isRemovable: false,
                    type: dataType,
                  })
                }}
                onUpdate={saveLabel}
                onMouseDown={(e) => {
                  multiSelectKeydownHandler(e as any, template)
                }}
                content={content}
                disabled={
                  !isEditorFocused && (isReadOnly || isSelectKeyPressed)
                }
                menuOffsetY={-20}
                placeholder="Write something..."
                isDragging={isDragging}
                updateBomWithSlashCommands={true}
                editorRef={editorRef}
                isTransparent
                backgroundColor={backgroundColor}
                updateBackgroundColorHandler={updateBackgroundColorHandler}
              />
            </>
          </Resizable>
        </div>
      </div>
    </OptionsContextMenu>
  )
}
